import React, { useState, useEffect, useMemo } from "react";
import {
  Switch,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Delete } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteAlert,
  getAllAlerts,
  toggleAlertStatus,
} from "../../../redux/actions/authActions";
import SearchBar from "../../../components/searchbar/SearchBar";

const AlertDatatable = () => {
  const dispatch = useDispatch();

  // 1. Get both alerts and the loading state from Redux
  const { allAlerts, loading } = useSelector((state) => state.auth);
  
  const [rows, setRows] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [open, setOpen] = useState(false);

  // Initial Fetch on Mount
  useEffect(() => {
    dispatch(getAllAlerts());
  }, [dispatch]);

  // Sync rows whenever Redux data changes
  useEffect(() => {
    if (Array.isArray(allAlerts)) {
      const formattedAlerts = allAlerts.map((alert) => ({
        ...alert,
        id: alert._id || alert.id,
      }));
      setRows(formattedAlerts);
    }
  }, [allAlerts]);

  const handleSearch = (inputValue) => {
    if (!inputValue) {
      setRows(allAlerts.map((a) => ({ ...a, id: a._id })));
      return;
    }
    const filteredRows = allAlerts
      .map((alert) => ({ ...alert, id: alert._id }))
      .filter((row) =>
        row.content?.toLowerCase().includes(inputValue.toLowerCase())
      );
    setRows(filteredRows);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    // Optimistic Update: Update local UI immediately
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, status: !currentStatus } : row))
    );
    
    // Dispatch action (ensure your action/reducer handles loading for this specifically)
    await dispatch(toggleAlertStatus(id, !currentStatus));
  };

  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setOpen(true);
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteAlert(deleteId));
      setOpen(false);
      // Local removal so we don't wait for a re-fetch
      setRows((prev) => prev.filter((row) => row.id !== deleteId));
    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  const columns = useMemo(() => [
    {
      field: "createdAt",
      headerName: "Created On",
      width: 180,
      renderCell: (params) => (params.value ? new Date(params.value).toLocaleDateString() : "N/A"),
    },
    { field: "content", headerName: "Alert", width: 300 },
    { field: "description", headerName: "Description", width: 350 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Switch
          checked={!!params.value}
          onChange={() => handleToggleStatus(params.row.id, params.value)}
          color="success"
        />
      ),
    },
    {
      field: "manage",
      headerName: "Manage",
      width: 100,
      renderCell: (params) => (
        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => handleOpenDeleteDialog(params.row.id)}>
            <Delete />
          </IconButton>
        </Tooltip>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <>
      <SearchBar
        titleOne="Add Alert"
        onSearch={handleSearch}
        addClientPath="/notifications/add-alert"
        showAddButton={true}
      />
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mt-4">
        <h2 className="text-xl font-semibold mb-4">Alerts List</h2>
        <div className="h-[500px] w-full">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading && rows.length === 0}
            pageSizeOptions={[5, 8, 10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 8, page: 0 } } }}
            sx={{ border: "none" }}
            disableRowSelectionOnClick
          />
        </div>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this alert?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AlertDatatable;